import * as Permissions from '@/js/libs/generated/permissions/types';
import * as Types from './types';
/** The direction to order by. */
export var OrderDirection;
(function (OrderDirection) {
    OrderDirection["ASC"] = "ASC";
    OrderDirection["DESC"] = "DESC";
})(OrderDirection || (OrderDirection = {}));
/**
 * Dimension to group the aggregation by.
 * If using time-based groupings (DAY, WEEK, MONTH), only one of these may be specified per request.
 */
export var ImpressionAggregatesGroupBy;
(function (ImpressionAggregatesGroupBy) {
    ImpressionAggregatesGroupBy["DAY"] = "DAY";
    ImpressionAggregatesGroupBy["WEEK"] = "WEEK";
    ImpressionAggregatesGroupBy["MONTH"] = "MONTH";
    ImpressionAggregatesGroupBy["PAGE_DOMAIN"] = "PAGE_DOMAIN";
    ImpressionAggregatesGroupBy["COUNTRY_CODE"] = "COUNTRY_CODE";
    ImpressionAggregatesGroupBy["DEVICE_CATEGORY"] = "DEVICE_CATEGORY";
})(ImpressionAggregatesGroupBy || (ImpressionAggregatesGroupBy = {}));
/** The category of the device. */
export var DeviceCategory;
(function (DeviceCategory) {
    DeviceCategory["DESKTOP"] = "DESKTOP";
    DeviceCategory["MOBILE"] = "MOBILE";
    DeviceCategory["TABLET"] = "TABLET";
    DeviceCategory["OTHER"] = "OTHER";
})(DeviceCategory || (DeviceCategory = {}));
/** Where the traffic comes from. WEB is header tag traffic, IOS and ANDROID are mobile SDK apps. */
export var Platform;
(function (Platform) {
    Platform["WEB"] = "WEB";
    Platform["IOS"] = "IOS";
    Platform["ANDROID"] = "ANDROID";
})(Platform || (Platform = {}));
/** The field to order by. Must be one of the groupBy dimensions or 'pageImpressionCount'. */
export var ImpressionAggregatesOrderByField;
(function (ImpressionAggregatesOrderByField) {
    ImpressionAggregatesOrderByField["TIMESTAMP"] = "TIMESTAMP";
    ImpressionAggregatesOrderByField["PAGE_DOMAIN"] = "PAGE_DOMAIN";
    ImpressionAggregatesOrderByField["COUNTRY_CODE"] = "COUNTRY_CODE";
    ImpressionAggregatesOrderByField["DEVICE_CATEGORY"] = "DEVICE_CATEGORY";
    ImpressionAggregatesOrderByField["PAGE_IMPRESSION_COUNT"] = "PAGE_IMPRESSION_COUNT";
})(ImpressionAggregatesOrderByField || (ImpressionAggregatesOrderByField = {}));
/**
 * The field to order by. Must be one of the groupBy dimensions or metric fields.
 * DEPRECATED value: 'BIDDER_CODE', with the group-by it orders — still accepted, not removed.
 */
export var AdImpressionAnalyticsOrderByField;
(function (AdImpressionAnalyticsOrderByField) {
    AdImpressionAnalyticsOrderByField["TIMESTAMP"] = "TIMESTAMP";
    AdImpressionAnalyticsOrderByField["SOURCE"] = "SOURCE";
    AdImpressionAnalyticsOrderByField["TAG_ID"] = "TAG_ID";
    AdImpressionAnalyticsOrderByField["DIV_ID"] = "DIV_ID";
    AdImpressionAnalyticsOrderByField["COMPANY_ID"] = "COMPANY_ID";
    AdImpressionAnalyticsOrderByField["DEVICE_CATEGORY"] = "DEVICE_CATEGORY";
    AdImpressionAnalyticsOrderByField["OS_NAME"] = "OS_NAME";
    AdImpressionAnalyticsOrderByField["BROWSER_NAME"] = "BROWSER_NAME";
    AdImpressionAnalyticsOrderByField["COUNTRY_CODE"] = "COUNTRY_CODE";
    AdImpressionAnalyticsOrderByField["BIDDER_CODE"] = "BIDDER_CODE";
    AdImpressionAnalyticsOrderByField["WINNER_TYPE"] = "WINNER_TYPE";
    AdImpressionAnalyticsOrderByField["WINNER_COMPANY_ID"] = "WINNER_COMPANY_ID";
    AdImpressionAnalyticsOrderByField["SYSTEM_WINNER"] = "SYSTEM_WINNER";
    AdImpressionAnalyticsOrderByField["TOTAL_EUR_CPM"] = "TOTAL_EUR_CPM";
    AdImpressionAnalyticsOrderByField["AVG_EUR_CPM"] = "AVG_EUR_CPM";
    AdImpressionAnalyticsOrderByField["AD_IMPRESSION_COUNT"] = "AD_IMPRESSION_COUNT";
    AdImpressionAnalyticsOrderByField["AUCTION_COUNT"] = "AUCTION_COUNT";
    AdImpressionAnalyticsOrderByField["REVENUE_EUR"] = "REVENUE_EUR";
    AdImpressionAnalyticsOrderByField["FILL_RATE"] = "FILL_RATE";
})(AdImpressionAnalyticsOrderByField || (AdImpressionAnalyticsOrderByField = {}));
/**
 * Dimension to group the aggregation by.
 * If using time-based groupings (HOUR, DAY, WEEK, MONTH, YEAR), only one of these may be specified per request.
 */
export var HeaderAnalyticsGroupBy;
(function (HeaderAnalyticsGroupBy) {
    HeaderAnalyticsGroupBy["HOUR"] = "HOUR";
    HeaderAnalyticsGroupBy["DAY"] = "DAY";
    HeaderAnalyticsGroupBy["WEEK"] = "WEEK";
    HeaderAnalyticsGroupBy["MONTH"] = "MONTH";
    HeaderAnalyticsGroupBy["YEAR"] = "YEAR";
    HeaderAnalyticsGroupBy["TAG_TYPE"] = "TAG_TYPE";
    HeaderAnalyticsGroupBy["TAG_ID"] = "TAG_ID";
    HeaderAnalyticsGroupBy["COMPANY_ID"] = "COMPANY_ID";
    HeaderAnalyticsGroupBy["DEVICE_CATEGORY"] = "DEVICE_CATEGORY";
    HeaderAnalyticsGroupBy["PLATFORM"] = "PLATFORM";
    HeaderAnalyticsGroupBy["COUNTRY_CODE"] = "COUNTRY_CODE";
    HeaderAnalyticsGroupBy["OS_NAME"] = "OS_NAME";
    HeaderAnalyticsGroupBy["BROWSER_NAME"] = "BROWSER_NAME";
})(HeaderAnalyticsGroupBy || (HeaderAnalyticsGroupBy = {}));
/**
 * Dimension to group the aggregation by.
 * If using time-based groupings (HOUR, DAY, WEEK, MONTH, YEAR), only one of these may be specified per request.
 * DEPRECATED value: 'BIDDER_CODE'. It still groups and is not removed. `fillRate` is null under
 * it — a placement's visibility carries no bidder, and a per-bidder ratio would be a Win Rate,
 * not a Fill Rate — while `auctionCount` and `requestedSlotCount` are still broken down by it.
 * The bidder label is also not comparable across the two stages it breaks down: `bidRequest`
 * carries the bidder alias, whereas `adImpression` can carry an aliased server-to-server
 * bidder's real name, so one bidder's requests and its impressions can land on two different
 * rows. Removal waits until the external service-account callers are identified —
 * `docs/bidder-dimension-deprecation.md`.
 */
export var AdImpressionAnalyticsGroupBy;
(function (AdImpressionAnalyticsGroupBy) {
    AdImpressionAnalyticsGroupBy["HOUR"] = "HOUR";
    AdImpressionAnalyticsGroupBy["DAY"] = "DAY";
    AdImpressionAnalyticsGroupBy["WEEK"] = "WEEK";
    AdImpressionAnalyticsGroupBy["MONTH"] = "MONTH";
    AdImpressionAnalyticsGroupBy["YEAR"] = "YEAR";
    AdImpressionAnalyticsGroupBy["SOURCE"] = "SOURCE";
    AdImpressionAnalyticsGroupBy["TAG_ID"] = "TAG_ID";
    AdImpressionAnalyticsGroupBy["DIV_ID"] = "DIV_ID";
    AdImpressionAnalyticsGroupBy["COMPANY_ID"] = "COMPANY_ID";
    AdImpressionAnalyticsGroupBy["DEVICE_CATEGORY"] = "DEVICE_CATEGORY";
    AdImpressionAnalyticsGroupBy["OS_NAME"] = "OS_NAME";
    AdImpressionAnalyticsGroupBy["BROWSER_NAME"] = "BROWSER_NAME";
    AdImpressionAnalyticsGroupBy["COUNTRY_CODE"] = "COUNTRY_CODE";
    AdImpressionAnalyticsGroupBy["BIDDER_CODE"] = "BIDDER_CODE";
    AdImpressionAnalyticsGroupBy["WINNER_TYPE"] = "WINNER_TYPE";
    AdImpressionAnalyticsGroupBy["WINNER_COMPANY_ID"] = "WINNER_COMPANY_ID";
    AdImpressionAnalyticsGroupBy["SYSTEM_WINNER"] = "SYSTEM_WINNER";
})(AdImpressionAnalyticsGroupBy || (AdImpressionAnalyticsGroupBy = {}));
/** The field to order by. Must be one of the groupBy dimensions or metric fields. */
export var HeaderAnalyticsOrderByField;
(function (HeaderAnalyticsOrderByField) {
    HeaderAnalyticsOrderByField["TIMESTAMP"] = "TIMESTAMP";
    HeaderAnalyticsOrderByField["TAG_TYPE"] = "TAG_TYPE";
    HeaderAnalyticsOrderByField["TAG_ID"] = "TAG_ID";
    HeaderAnalyticsOrderByField["COMPANY_ID"] = "COMPANY_ID";
    HeaderAnalyticsOrderByField["DEVICE_CATEGORY"] = "DEVICE_CATEGORY";
    HeaderAnalyticsOrderByField["PLATFORM"] = "PLATFORM";
    HeaderAnalyticsOrderByField["COUNTRY_CODE"] = "COUNTRY_CODE";
    HeaderAnalyticsOrderByField["OS_NAME"] = "OS_NAME";
    HeaderAnalyticsOrderByField["BROWSER_NAME"] = "BROWSER_NAME";
    HeaderAnalyticsOrderByField["HEADER_LOADED_COUNT"] = "HEADER_LOADED_COUNT";
    HeaderAnalyticsOrderByField["PAGE_IMPRESSION_COUNT"] = "PAGE_IMPRESSION_COUNT";
    HeaderAnalyticsOrderByField["VISITOR_COUNT"] = "VISITOR_COUNT";
    HeaderAnalyticsOrderByField["SESSION_COUNT"] = "SESSION_COUNT";
})(HeaderAnalyticsOrderByField || (HeaderAnalyticsOrderByField = {}));
